import { apiClient } from './client';
import type { RequestOptions } from '@/types/api';
import type {
  OriginGroup,
  OriginGroupListResponse,
  OriginGroupDetailResponse,
  CreateOriginGroupRequest,
  UpdateOriginGroupRequest,
  ConnectionTestRequest,
  ConnectionTestResponse,
  HealthCheckHistoryResponse,
  OriginGroupFilters,
  TriggerHealthCheckResponse,
} from '@/types/originGroup';

/**
 * Origin Groups API functions for the CDN Manager
 */
export const originGroupsApi = {
  /**
   * Fetch origin groups with optional pagination and filters
   */
  async getAll(
    filters?: OriginGroupFilters,
    page = 1,
    limit = 20,
    options?: RequestOptions
  ): Promise<OriginGroupListResponse> {
    const params: Record<string, string> = {
      page: page.toString(),
      limit: limit.toString(),
    };

    if (filters?.health_status) {
      params.health_status = filters.health_status;
    }
    if (filters?.enable_tls !== undefined) {
      params.enable_tls = filters.enable_tls.toString();
    }
    if (filters?.search) {
      params.search = filters.search;
    }

    const response = await apiClient.get<OriginGroupListResponse>('/origin-groups', {
      params,
      signal: options?.signal,
    });
    return response.data;
  },

  /**
   * Fetch a single origin group by ID
   */
  async getById(id: string, options?: RequestOptions): Promise<OriginGroup> {
    const response = await apiClient.get<OriginGroupDetailResponse>(`/origin-groups/${id}`, {
      signal: options?.signal,
    });
    return response.data.origin_group;
  },

  /**
   * Create a new origin group
   */
  async create(data: CreateOriginGroupRequest): Promise<OriginGroup> {
    const response = await apiClient.post<OriginGroupDetailResponse>('/origin-groups', data);
    return response.data.origin_group;
  },

  /**
   * Update an existing origin group
   */
  async update(id: string, data: UpdateOriginGroupRequest): Promise<OriginGroup> {
    const response = await apiClient.put<OriginGroupDetailResponse>(`/origin-groups/${id}`, data);
    return response.data.origin_group;
  },

  /**
   * Delete an origin group
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/origin-groups/${id}`);
  },

  /**
   * Test connection to an origin group
   */
  async testConnection(id: string, data?: ConnectionTestRequest): Promise<ConnectionTestResponse> {
    const response = await apiClient.post<ConnectionTestResponse>(
      `/origin-groups/${id}/test`,
      data || {}
    );
    return response.data;
  },

  /**
   * Get health check history for an origin group
   */
  async getHealthHistory(id: string, options?: RequestOptions): Promise<HealthCheckHistoryResponse> {
    const response = await apiClient.get<HealthCheckHistoryResponse>(`/origin-groups/${id}/health`, {
      signal: options?.signal,
    });
    return response.data;
  },

  /**
   * Trigger an immediate health check for an origin group
   */
  async triggerHealthCheck(id: string): Promise<TriggerHealthCheckResponse> {
    const response = await apiClient.post<TriggerHealthCheckResponse>(
      `/origin-groups/${id}/health/check`
    );
    return response.data;
  },
};
